import { EditorSelection, type EditorState, type TransactionSpec } from '@codemirror/state';
import type { EditorView, KeyBinding } from '@codemirror/view';

/**
 * Editing helpers for GFM pipe tables.
 *
 * Every action works the same way: find the table around the cursor, turn it
 * into rows of cells, change the model, and write the whole table back padded
 * into aligned columns. Rewriting the whole block keeps the source readable and
 * means no action has to reason about half-aligned pipes.
 *
 * Each function takes a state and returns a transaction, or null when the
 * cursor is not in a table — so a key binding can fall through to its default
 * (Tab indents outside tables) and the logic is testable without a DOM.
 */

export type Align = 'none' | 'left' | 'center' | 'right';

export interface TableModel {
  /** Header first, then the body rows. The delimiter row is `aligns`. */
  rows: string[][];
  aligns: Align[];
}

interface Located {
  model: TableModel;
  from: number;
  to: number;
  /** Row in `rows` (0 = header); the delimiter line counts as the header. */
  row: number;
  col: number;
}

const DELIMITER_CELL = /^\s*:?-+:?\s*$/;

/** Split one table line into cells, honouring `\|` and pipes inside code spans. */
export function splitRow(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inCode = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '\\' && line[i + 1] === '|') {
      current += '\\|';
      i++;
    } else if (ch === '`') {
      inCode = !inCode;
      current += ch;
    } else if (ch === '|' && !inCode) {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  // A leading and trailing pipe leave empty edges; they are not cells.
  const trimmed = line.trim();
  if (trimmed.startsWith('|')) cells.shift();
  if (trimmed.endsWith('|') && !trimmed.endsWith('\\|') && cells.length > 0) cells.pop();
  return cells.map((c) => c.trim());
}

function isDelimiterRow(line: string): boolean {
  if (!line.includes('-')) return false;
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => DELIMITER_CELL.test(c));
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (left) return 'left';
  if (right) return 'right';
  return 'none';
}

/** Which cell a column offset within a raw table line falls in. */
function cellIndexAt(line: string, offset: number): number {
  const trimmedStart = line.length - line.trimStart().length;
  const leadingPipe = line.trimStart().startsWith('|');
  let index = 0;
  let inCode = false;
  for (let i = 0; i < Math.min(offset, line.length); i++) {
    const ch = line[i];
    if (ch === '\\' && line[i + 1] === '|') i++;
    else if (ch === '`') inCode = !inCode;
    else if (ch === '|' && !inCode && !(leadingPipe && i === trimmedStart)) index++;
  }
  return index;
}

/** Find the table the main cursor is in, if any. */
export function locateTable(state: EditorState): Located | null {
  const pos = state.selection.main.head;
  const cursorLine = state.doc.lineAt(pos);
  const isTableLine = (text: string) => text.trim() !== '' && text.includes('|');
  if (!isTableLine(cursorLine.text)) return null;

  let first = cursorLine.number;
  while (first > 1 && isTableLine(state.doc.line(first - 1).text)) first--;
  let last = cursorLine.number;
  while (last < state.doc.lines && isTableLine(state.doc.line(last + 1).text)) last++;

  // The block may start above the table (a paragraph with a pipe in it), so the
  // header is whichever line sits right above the first delimiter row.
  let header = -1;
  for (let n = first; n < last; n++) {
    if (isDelimiterRow(state.doc.line(n + 1).text)) {
      header = n;
      break;
    }
  }
  if (header === -1 || cursorLine.number < header) return null;

  const lines: string[] = [];
  for (let n = header; n <= last; n++) lines.push(state.doc.line(n).text);
  const [headerText = '', delimiterText = '', ...body] = lines;
  const width = Math.max(
    splitRow(headerText).length,
    splitRow(delimiterText).length,
    ...body.map((l) => splitRow(l).length),
  );
  const pad = (cells: string[]) => [...cells, ...Array<string>(width - cells.length).fill('')];
  const aligns = pad(splitRow(delimiterText)).map(alignOf);
  const rows = [headerText, ...body].map((l) => pad(splitRow(l)).slice(0, width));

  const lineIndex = cursorLine.number - header;
  const row = lineIndex <= 1 ? 0 : lineIndex - 1;
  const col = Math.min(cellIndexAt(cursorLine.text, pos - cursorLine.from), width - 1);

  return {
    model: { rows, aligns },
    from: state.doc.line(header).from,
    to: state.doc.line(last).to,
    row,
    col,
  };
}

function delimiter(align: Align, width: number): string {
  const dashes = '-'.repeat(width);
  if (align === 'left') return `:${dashes.slice(1)}`;
  if (align === 'right') return `${dashes.slice(1)}:`;
  if (align === 'center') return `:${dashes.slice(2)}:`;
  return dashes;
}

function padCell(text: string, width: number, align: Align): string {
  const space = width - text.length;
  if (align === 'right') return ' '.repeat(space) + text;
  if (align === 'center') {
    const left = Math.floor(space / 2);
    return ' '.repeat(left) + text + ' '.repeat(space - left);
  }
  return text + ' '.repeat(space);
}

/** Write a model back as aligned Markdown, one line per row. */
export function formatTable({ rows, aligns }: TableModel): {
  text: string;
  /** Offset of each cell's content start, per row, relative to the table start. */
  cellStarts: number[][];
  widths: number[];
} {
  const widths = aligns.map((_, c) => Math.max(3, ...rows.map((r) => (r[c] ?? '').length)));
  const lines: string[] = [];
  const cellStarts: number[][] = [];
  let offset = 0;
  const line = (cells: string[], contents: string[] | null) => {
    const text = `| ${cells.join(' | ')} |`;
    if (contents) {
      const row: number[] = [];
      let at = offset + 2;
      contents.forEach((content, c) => {
        const width = widths[c] ?? 3;
        // Where the content sits inside its padded cell, so an empty cell puts
        // the cursor at the start rather than after the padding.
        const space = width - content.length;
        const align = aligns[c] ?? 'none';
        const lead = align === 'right' ? space : align === 'center' ? Math.floor(space / 2) : 0;
        row.push(at + lead);
        at += width + 3;
      });
      cellStarts.push(row);
    }
    lines.push(text);
    offset += text.length + 1;
  };

  rows.forEach((cells, r) => {
    line(
      cells.map((cell, c) => padCell(cell, widths[c] ?? 3, aligns[c] ?? 'none')),
      cells,
    );
    if (r === 0)
      line(
        aligns.map((a, c) => delimiter(a, widths[c] ?? 3)),
        null,
      );
  });
  return { text: lines.join('\n'), cellStarts, widths };
}

/** Replace the table and select the content of cell (row, col). */
function write(state: EditorState, at: Located, model: TableModel, row: number, col: number) {
  const { text, cellStarts } = formatTable(model);
  const start = at.from + (cellStarts[row]?.[col] ?? 2);
  const content = model.rows[row]?.[col] ?? '';
  const spec: TransactionSpec = {
    changes: { from: at.from, to: at.to, insert: text },
    selection: EditorSelection.single(start, start + content.length),
    scrollIntoView: true,
    userEvent: 'input.table',
  };
  // Unchanged text still moves the selection; skipping the change keeps undo
  // history free of no-op entries.
  if (state.sliceDoc(at.from, at.to) === text) delete spec.changes;
  return spec;
}

const clone = (m: TableModel): TableModel => ({
  rows: m.rows.map((r) => [...r]),
  aligns: [...m.aligns],
});

export type TableAction =
  | 'nextCell'
  | 'previousCell'
  | 'format'
  | 'addRowBelow'
  | 'addColumnRight'
  | 'deleteRow'
  | 'deleteColumn'
  | 'cycleAlign';

export function tableTransaction(state: EditorState, action: TableAction): TransactionSpec | null {
  const at = locateTable(state);
  if (!at) return null;
  const model = clone(at.model);
  const cols = model.aligns.length;
  const { row, col } = at;

  switch (action) {
    case 'format':
      return write(state, at, model, row, col);
    case 'nextCell': {
      if (col + 1 < cols) return write(state, at, model, row, col + 1);
      // Past the last cell of the last row, Tab grows the table — the way a
      // spreadsheet does, so a table can be filled in without leaving the keys.
      if (row + 1 >= model.rows.length) model.rows.push(Array<string>(cols).fill(''));
      return write(state, at, model, row + 1, 0);
    }
    case 'previousCell': {
      if (col > 0) return write(state, at, model, row, col - 1);
      if (row > 0) return write(state, at, model, row - 1, cols - 1);
      return write(state, at, model, 0, 0);
    }
    case 'addRowBelow':
      model.rows.splice(row + 1, 0, Array<string>(cols).fill(''));
      return write(state, at, model, row + 1, col);
    case 'addColumnRight':
      model.rows.forEach((r) => r.splice(col + 1, 0, ''));
      model.aligns.splice(col + 1, 0, 'none');
      return write(state, at, model, row, col + 1);
    case 'deleteRow':
      // The header is what makes it a table; removing it would turn the rest
      // into a paragraph of pipes.
      if (row === 0) return null;
      model.rows.splice(row, 1);
      return write(state, at, model, Math.min(row, model.rows.length - 1), col);
    case 'deleteColumn':
      if (cols === 1) return null;
      model.rows.forEach((r) => r.splice(col, 1));
      model.aligns.splice(col, 1);
      return write(state, at, model, row, Math.min(col, cols - 2));
    case 'cycleAlign': {
      const order: Align[] = ['none', 'left', 'center', 'right'];
      const current = model.aligns[col] ?? 'none';
      model.aligns[col] = order[(order.indexOf(current) + 1) % order.length] ?? 'none';
      return write(state, at, model, row, col);
    }
  }
}

/** A starter table at the cursor, on its own lines. */
export function insertTableTransaction(state: EditorState): TransactionSpec {
  const pos = state.selection.main.from;
  const line = state.doc.lineAt(pos);
  const before = line.text.trim() === '' ? '' : '\n\n';
  const at = line.text.trim() === '' ? line.from : line.to;
  const { text, cellStarts } = formatTable({
    rows: [
      ['Column', 'Column'],
      ['', ''],
    ],
    aligns: ['none', 'none'],
  });
  const start = at + before.length + (cellStarts[0]?.[0] ?? 2);
  return {
    changes: { from: at, to: line.text.trim() === '' ? line.to : at, insert: `${before}${text}\n` },
    selection: EditorSelection.single(start, start + 'Column'.length),
    scrollIntoView: true,
    userEvent: 'input.table',
  };
}

/** Everything the palette can ask of a table: the actions above, or a new one. */
export type TableCommand = TableAction | 'insert';

/** Run a table command on a view; false when it needs a table and there is none. */
export function runTableCommand(view: EditorView, command: TableCommand): boolean {
  const spec =
    command === 'insert'
      ? insertTableTransaction(view.state)
      : tableTransaction(view.state, command);
  if (!spec) return false;
  view.dispatch(spec);
  view.focus();
  return true;
}

/**
 * Tab and Shift-Tab move between cells inside a table and fall through
 * everywhere else, where Tab keeps indenting.
 */
export const tableKeymap: KeyBinding[] = [
  { key: 'Tab', run: (view) => runTableCommand(view, 'nextCell') },
  { key: 'Shift-Tab', run: (view) => runTableCommand(view, 'previousCell') },
];
